import { h, Component, createRef } from "preact";

import { Keyboard } from "../../foundation/enums";
import classname from "../../foundation/utils/classname";
import { applyReadOnlyUntilFocusToDisableAutofillInSafari } from "../hooks/useDisableAutoFillSafariBrowsers";
import DropdownMenu from "../v2/DropdownMenu";
import IconArrowDown from "../v2/Icons/IconArrowDown";
import Menu from "../v2/Menu";

import "./style.css";

interface Props {
    class?: string;
    placeholder?: string;
    value: number | string;
    disabled?: boolean;
    options: (number | string)[];
    shouldChangeOnInput?: boolean;
    onChange: (value: (number | string)) => void;
    [rest: string]: unknown;
}

interface State {
    value: number | string;
    dropdownOpen: boolean;
}

export default class InputWithDropdown extends Component<Props> {
    cleanUp: (() => void | undefined) | undefined;

    static override defaultProps = {
        type: "text",
        min: 0
    };

    override state = {
        value: this.props.value,
        dropdownOpen: false
    };

    inputRef = createRef();

    handleBlur = (event: Event) => {
        const { value: oldValue, onChange } = this.props;
        const { value: stateValue } = this.state;
        const { type, value, valueAsNumber } = event.target as HTMLInputElement;
        const trimmedValue = value.trim();

        if (oldValue === trimmedValue || oldValue === valueAsNumber) {
            return;
        }

        const valueAsNumberWithNaNCheck = Number.isNaN(valueAsNumber) ? 0 : valueAsNumber;
        const newValue = type === "number" ? valueAsNumberWithNaNCheck : trimmedValue;

        if (value === "") {
            this.setState({ value: oldValue });
        } else {
            if (trimmedValue !== stateValue) {
                this.setState({ value: trimmedValue });
            }

            onChange(newValue);
        }
    };

    handleKeyDown = (event: KeyboardEvent) => {
        switch (event.key) {
            case Keyboard.Key.Enter:
                (event.target as HTMLInputElement).blur();
                break;

            case Keyboard.Key.Escape:
                this.setState({ value: this.props.value }, () => {
                    (event.target as HTMLInputElement).blur();
                });
                break;

            default:
                // no-op
        }
    };

    handleInput = (event: Event) => {
        const { onChange, shouldChangeOnInput } = this.props;
        const { value: oldValue } = this.state;
        const input = event.target as HTMLInputElement;
        const {
            type,
            value,
            valueAsNumber,
            min,
            max,
            validity
        } = input;
        let newValue;

        if (validity.rangeUnderflow) {
            newValue = min;
        } else if (validity.rangeOverflow) {
            newValue = max;
        } else if (validity.badInput) {
            newValue = oldValue;
        } else if (validity.valueMissing) {
            newValue = "";
        } else {
            newValue = type === "number"
                ? String(parseInt(value, 10))
                : value;
        }

        this.setState({ value: newValue });

        if (shouldChangeOnInput && newValue !== oldValue) {
            const valueAsNumberWithNaNCheck = Number.isNaN(valueAsNumber) ? 0 : valueAsNumber;
            const parsedValue = type === "number" ? valueAsNumberWithNaNCheck : value.trim();
            onChange(parsedValue);
        }
    };

    handleChange = (value: number | string) => {
        const { value: oldValue, onChange } = this.props;

        if (oldValue === value) {
            return;
        }

        this.setState({ value });
        onChange(value);
    };

    handleDropdownVisibilityChange = (dropdownOpen: boolean) => {
        this.setState({
            dropdownOpen
        });
    };

    createOnClickHandler = (value: string | number) => () => {
        this.handleChange(value);
    };

    override componentDidMount() {
        const { disabled, autoFocus } = this.props;

        if (this.inputRef.current) {
            const { cleanUp } = applyReadOnlyUntilFocusToDisableAutofillInSafari(this.inputRef.current) ?? {};

            if (cleanUp) {
                this.cleanUp = cleanUp;
            }
        }

        if (!disabled && autoFocus) {
            this.inputRef.current.focus();
        }
    }

    override componentDidUpdate(prevProps: Props, prevState: State) {
        const { value, disabled, autoFocus, shouldChangeOnInput } = this.props;
        const { value: stateValue } = this.state;

        if (prevProps.value !== value && !shouldChangeOnInput) {
            this.setState({ value });
        } else if (stateValue !== value && stateValue === prevState.value) {
            this.setState({ value });
        }

        if (prevProps.disabled && !disabled && autoFocus) {
            this.inputRef.current.focus();
        }
    }

    override componentWillUnmount() {
        if (this.cleanUp) {
            this.cleanUp();
        }
    }

    render() {
        const {
            class: cssClass,
            placeholder,
            disabled,
            options,
            onChange: _onChange,
            ...rest
        } = this.props;

        const { value, dropdownOpen } = this.state;

        return (
            <div class={classname("inputWithDropdownContainer", cssClass, { disabled, dropdownOpen })}>
                <div class="inputContainer">
                    <div class="mirror">{value || placeholder || " "}</div>
                    <input
                        name="dummy_name_to_prevent_autofill" // prevents safari auto fill
                        {...rest}
                        ref={this.inputRef}
                        class="inputWithDropdownInput"
                        placeholder={placeholder}
                        value={value}
                        disabled={disabled}
                        required
                        spellcheck={false}
                        autoCorrect="off"
                        autoComplete="off"
                        /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
                        // @ts-ignore
                        autocapitalize="off"
                        onInput={this.handleInput}
                        onKeyDown={this.handleKeyDown}
                        onBlur={this.handleBlur}/>
                </div>
                <DropdownMenu
                    buttonProps={{
                        class: "inputWithDropdownDropdown",
                        disabled,
                        children: <IconArrowDown/>,
                        showChevron: false
                    }}
                    menuProps={{
                        applyMaxHeight: true
                    }}
                    onMenuStateChange={this.handleDropdownVisibilityChange}>
                    {options.map(option => (
                        <Menu.Button
                            key={option}
                            checked={option === value}
                            onClick={this.createOnClickHandler(option)}>
                            {option}
                        </Menu.Button>
                    ))}
                </DropdownMenu>
            </div>
        );
    }
}
